import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { portalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';

export function PortalEstimatesPage() {
  const estimatesQuery = useQuery({
    queryKey: ['portal', 'estimates'],
    queryFn: portalApi.estimates,
  });

  const estimates = (estimatesQuery.data ?? []) as Array<{
    id: string;
    estimateNumber: string;
    status: string;
    ticketNumber: string | null;
    deviceSummary: string;
    services: Array<{ serviceName: string; price: number }>;
    total: string;
    validUntil: string | null;
    approvalToken: string | null;
  }>;

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">Estimates</h1>
      <div className="space-y-3">
        {estimates.map((e) => (
          <div key={e.id} className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm">
            <div className="flex justify-between gap-3">
              <div className="font-bold">{e.estimateNumber}</div>
              <span className="text-xs font-semibold text-amber-700">Awaiting Approval</span>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {e.deviceSummary}
              {e.ticketNumber ? ` · ${e.ticketNumber}` : ''}
            </p>
            <ul className="mt-3 space-y-1 text-sm">
              {e.services?.map((s) => (
                <li key={s.serviceName} className="flex justify-between">
                  <span>{s.serviceName}</span>
                  <span>{formatCurrency(s.price)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between border-t border-slate-100 pt-2 font-bold">
              <span>Total</span>
              <span>{formatCurrency(e.total)}</span>
            </div>
            {e.validUntil ? (
              <p className="mt-2 text-sm text-slate-500">Valid until: {formatDateTime(e.validUntil)}</p>
            ) : null}
            {e.approvalToken ? (
              <div className="mt-4 flex gap-2">
                <Link
                  to={`/estimates/approve/${e.approvalToken}?action=decline`}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  ✗ Decline
                </Link>
                <Link
                  to={`/estimates/approve/${e.approvalToken}`}
                  className="rounded-xl bg-[#1B4F8A] px-4 py-2 text-sm font-semibold text-white"
                >
                  ✓ Approve Estimate →
                </Link>
              </div>
            ) : null}
          </div>
        ))}
        {estimates.length === 0 && !estimatesQuery.isLoading ? (
          <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-500">
            No pending estimates.
          </p>
        ) : null}
      </div>
    </div>
  );
}
